import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Star, Tags } from "lucide-react";
import * as React from "react";

import { ApiError } from "@/api/client";
import { catalog } from "@/api/endpoints";
import type { Category } from "@/api/types";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/ui/feedback";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";

export default function Catalog() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({ queryKey: ["categories"], queryFn: catalog.categories });

  const update = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Parameters<typeof catalog.setMerchandising>[1];
    }) => catalog.setMerchandising(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
    onError: (err) =>
      toast.error(
        "Could not update the category",
        err instanceof ApiError ? err.message : undefined,
      ),
  });

  // Sort by the order the app will render them in, then by name for stability.
  const sorted = React.useMemo(() => {
    const rows = [...(query.data ?? [])];
    rows.sort(
      (a, b) =>
        (a.display_order ?? 0) - (b.display_order ?? 0) ||
        a.name.localeCompare(b.name),
    );
    return rows;
  }, [query.data]);

  function move(index: number, direction: -1 | 1) {
    const target = sorted[index + direction];
    const current = sorted[index];
    if (!target || !current) return;
    // Swap the two display_order values.
    update.mutate({
      id: current.id,
      body: { display_order: target.display_order ?? index + direction },
    });
    update.mutate({
      id: target.id,
      body: { display_order: current.display_order ?? index },
    });
  }

  return (
    <>
      <PageHeader
        title="Categories"
        description="Controls what farmers see and in what order. Changes reach the app immediately — no release needed."
      />

      <Card className="overflow-hidden">
        {query.isLoading ? (
          <TableSkeleton cols={5} />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        ) : sorted.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">Order</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Featured</TableHead>
                <TableHead>Visible in app</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((c: Category, i) => (
                <TableRow key={c.id}>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7"
                        disabled={i === 0 || update.isPending}
                        onClick={() => move(i, -1)}
                        aria-label={`Move ${c.name} up`}
                      >
                        <ArrowUp className="size-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7"
                        disabled={i === sorted.length - 1 || update.isPending}
                        onClick={() => move(i, 1)}
                        aria-label={`Move ${c.name} down`}
                      >
                        <ArrowDown className="size-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{c.name}</div>
                    {c.name_hi && (
                      <div className="text-xs text-muted-foreground">{c.name_hi}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{c.slug}</TableCell>
                  <TableCell>
                    <Button
                      variant={c.is_featured ? "default" : "outline"}
                      size="sm"
                      disabled={update.isPending}
                      onClick={() =>
                        update.mutate({
                          id: c.id,
                          body: { is_featured: !c.is_featured },
                        })
                      }
                    >
                      <Star className={c.is_featured ? "fill-current" : ""} />
                      {c.is_featured ? "Featured" : "Feature"}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <button
                      type="button"
                      disabled={update.isPending}
                      onClick={() =>
                        update.mutate({
                          id: c.id,
                          body: { is_active: !c.is_active },
                        })
                      }
                      className="disabled:opacity-50"
                    >
                      <Badge variant={c.is_active ? "good" : "default"}>
                        {c.is_active ? "Visible" : "Hidden"}
                      </Badge>
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <EmptyState
            icon={Tags}
            title="No categories yet"
            description="Categories are created through the catalog API."
          />
        )}
      </Card>
    </>
  );
}
